// Quản lý phiên đăng nhập + refresh token.
// Single-flight bằng promise (thay cho isRefreshing bool + failedQueue thủ công),
// refresh call đi qua httpBare (không interceptor) để chống deadlock self-queue.
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'

import { notify } from '@/components/ui/ui-react-antd/NotificationCustom'
import ROUTER from '@/routes/ROUTER'

import {
  STORAGE,
  deleteAuthToken,
  deleteLocalStorage,
  deleteSessionStorage,
  getRefreshToken,
  setAuthToken,
  setRefreshToken,
} from '../../utils/storage'
import identityService from '../IdentityService'

// Nơi lưu token do STORAGE.REMEMBER_HOME (đặt lúc login) quyết định — setAuthToken/
// setRefreshToken tự đọc flag đó, không đoán lại theo nơi token đang nằm nữa.
const saveTokens = (accessToken: string, refreshToken?: string): void => {
  setAuthToken(accessToken)
  if (refreshToken) {
    setRefreshToken(refreshToken)
  }
}

const clearAllTokens = (): void => {
  deleteAuthToken()
  deleteLocalStorage(STORAGE.REFRESH_TOKEN)
  deleteSessionStorage(STORAGE.REFRESH_TOKEN)
}

// Thoát phiên: xoá token, thông báo, đưa về trang chủ (login).
export const logoutSession = (): void => {
  clearAllTokens()
  notify.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.')
  window.location.href = ROUTER.HOME
}

// ---- Single-flight refresh ----
let refreshPromise: Promise<string> | null = null

const doRefresh = async (): Promise<string> => {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    logoutSession()
    throw new Error('Refresh token không tồn tại')
  }

  try {
    const response = await identityService.refreshToken(refreshToken)
    const newRefreshToken = response.object?.refreshToken ?? response.object?.RefreshToken
    if (!response.isError && response.object?.token) {
      saveTokens(response.object.token, newRefreshToken)
      return response.object.token
    }
  } catch {
    // rơi xuống logoutSession bên dưới
  }

  logoutSession()
  throw new Error('Refresh token thất bại')
}

// Nếu đang refresh thì trả về CÙNG promise (dedupe concurrent 401),
// ngược lại khởi tạo refresh mới và tự reset sau khi settle.
export const ensureValidToken = (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

// Retry request sau khi có token mới. Gọi từ response interceptor khi gặp 401.
export const retryWithFreshToken = async (
  config: InternalAxiosRequestConfig & { _retry?: boolean; _skipAuth?: boolean },
  instance: AxiosInstance
): Promise<AxiosResponse> => {
  const token = await ensureValidToken()
  config._retry = true
  config.headers.Authorization = `Bearer ${token}`
  return instance(config)
}

// Normalize auth error: HTTP 401 hoặc envelope { statusCode: 401 }.
export const isAuthError = (status: number, data?: any): boolean => {
  if (status === 401) return true
  const envelopeStatus = Number(data?.statusCode ?? data?.StatusCode)
  return envelopeStatus === 401
}
